import {
  adminCatalogActorSchema,
  adminCatalogBatchDetailSchema,
  adminCatalogChangeSchema,
  adminCatalogEventSchema,
  adminCatalogRevertResultSchema,
  adminCatalogSummarySchema,
  type AdminCatalogActor,
  type AdminCatalogBatchDetail,
  type AdminCatalogChange,
  type AdminCatalogEvent,
  type AdminCatalogRevertResult,
  type AdminCatalogSummary,
  type CatalogEventKind,
  type CatalogSubject,
} from '@steado/admin-contract/catalog-history';
import { paginationMetaSchema, type PaginationMeta } from '@steado/admin-contract/pagination';
import { infiniteQueryOptions, queryOptions, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient } from '@/lib/api/client';

/**
 * Reads and writes for catalog History: one food's or exercise's changes,
 * the Catalog changes screen's events, and Revert.
 */

export interface Page<T> {
  readonly items: T[];
  readonly pagination: PaginationMeta;
}

/** Enough for most foods' whole history on the first page. */
export const HISTORY_PAGE_SIZE = 100;
export const EVENTS_PAGE_SIZE = 30;

async function page<T extends z.ZodTypeAny>(
  api: ApiClient,
  path: string,
  query: Record<string, string | number | undefined>,
  item: T,
): Promise<Page<z.infer<T>>> {
  const response = await api.requestWithMeta({ path, query, schema: z.array(item) });
  return { items: response.data, pagination: paginationMetaSchema.parse(response.meta.pagination) };
}

/** Every change to one food or exercise (its portions, names, recipe or edges too), newest first. */
export function subjectHistoryQueryOptions(api: ApiClient, subject: CatalogSubject, slug: string) {
  return infiniteQueryOptions({
    queryKey: ['admin', 'catalog', 'history', subject, slug],
    queryFn: ({ pageParam }): Promise<Page<AdminCatalogChange>> =>
      page(
        api,
        '/admin/catalog/changes',
        { subject, slug, limit: HISTORY_PAGE_SIZE, cursor: pageParam },
        adminCatalogChangeSchema,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) =>
      last.pagination.hasMore ? (last.pagination.nextCursor ?? undefined) : undefined,
  });
}

/** The events that changed one food or exercise: titles, batch notes and what a revert put back. */
export function subjectEventsQueryOptions(api: ApiClient, subject: CatalogSubject, slug: string) {
  return queryOptions({
    queryKey: ['admin', 'catalog', 'subject-events', subject, slug],
    queryFn: async (): Promise<AdminCatalogEvent[]> =>
      (await page(api, '/admin/catalog/events', { subject, slug, limit: 100 }, adminCatalogEventSchema))
        .items,
  });
}

/** One event by its batch id, for a link that opens an event the list has not loaded. */
export function eventByBatchQueryOptions(api: ApiClient, batchId: string | null) {
  return queryOptions({
    queryKey: ['admin', 'catalog', 'event', batchId],
    queryFn: async (): Promise<AdminCatalogEvent | null> =>
      (
        await page(
          api,
          '/admin/catalog/events',
          { batchId: batchId ?? undefined, limit: 1 },
          adminCatalogEventSchema,
        )
      ).items[0] ?? null,
    enabled: batchId !== null,
  });
}

export interface EventFilters {
  readonly kind?: CatalogEventKind | undefined;
  readonly actor?: string | undefined;
  readonly since?: string | undefined;
  readonly search?: string | undefined;
}

export function eventsQueryOptions(api: ApiClient, filters: EventFilters) {
  return infiniteQueryOptions({
    queryKey: ['admin', 'catalog', 'events', filters],
    queryFn: ({ pageParam }): Promise<Page<AdminCatalogEvent>> =>
      page(
        api,
        '/admin/catalog/events',
        {
          kind: filters.kind,
          actor: filters.actor,
          since: filters.since,
          search: filters.search,
          limit: EVENTS_PAGE_SIZE,
          cursor: pageParam,
        },
        adminCatalogEventSchema,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) =>
      last.pagination.hasMore ? (last.pagination.nextCursor ?? undefined) : undefined,
  });
}

/** One event's changes, oldest first: a pushed batch's detail, or any other event's changes. */
export function eventChangesQueryOptions(api: ApiClient, batchId: string | null) {
  return queryOptions({
    queryKey: ['admin', 'catalog', 'event-changes', batchId],
    queryFn: async (): Promise<AdminCatalogChange[]> => {
      const result = await page(
        api,
        '/admin/catalog/changes',
        { batchId: batchId ?? undefined, limit: 100 },
        adminCatalogChangeSchema,
      );
      return [...result.items].reverse();
    },
    enabled: batchId !== null,
  });
}

export function batchQueryOptions(api: ApiClient, batchId: string | null) {
  return queryOptions({
    queryKey: ['admin', 'catalog', 'batch', batchId],
    queryFn: (): Promise<AdminCatalogBatchDetail> =>
      api.request({ path: `/admin/catalog/batches/${batchId ?? ''}`, schema: adminCatalogBatchDetailSchema }),
    enabled: batchId !== null,
  });
}

export function summaryQueryOptions(api: ApiClient, since: string | undefined) {
  return queryOptions({
    queryKey: ['admin', 'catalog', 'summary', since ?? null],
    queryFn: (): Promise<AdminCatalogSummary> =>
      api.request({ path: '/admin/catalog/summary', query: { since }, schema: adminCatalogSummarySchema }),
  });
}

export function actorsQueryOptions(api: ApiClient) {
  return queryOptions({
    queryKey: ['admin', 'catalog', 'actors'],
    queryFn: (): Promise<AdminCatalogActor[]> =>
      api.request({ path: '/admin/catalog/actors', schema: z.array(adminCatalogActorSchema) }),
    staleTime: 5 * 60_000,
  });
}

/** One change, or some of an update's columns (a nutrition column brings its whole block). */
export function revertChange(
  api: ApiClient,
  changeId: string,
  fields?: readonly string[],
): Promise<AdminCatalogRevertResult> {
  return api.request({
    path: `/admin/catalog/changes/${changeId}/revert`,
    method: 'POST',
    body: fields === undefined ? {} : { fields },
    schema: adminCatalogRevertResultSchema,
  });
}

/** Several changes together, as one revert: an event's Revert all. */
export function revertChanges(api: ApiClient, ids: readonly string[]): Promise<AdminCatalogRevertResult> {
  return api.request({
    path: '/admin/catalog/changes/revert',
    method: 'POST',
    body: { ids },
    schema: adminCatalogRevertResultSchema,
  });
}

export function revertBatch(api: ApiClient, batchId: string): Promise<AdminCatalogRevertResult> {
  return api.request({
    path: `/admin/catalog/batches/${batchId}/revert`,
    method: 'POST',
    body: {},
    schema: adminCatalogRevertResultSchema,
  });
}

/** After any catalog write: every History, the events, and the records they describe. */
export function invalidateCatalog(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['admin', 'catalog'] });
  void queryClient.invalidateQueries({ queryKey: ['admin', 'food'] });
  void queryClient.invalidateQueries({ queryKey: ['admin', 'foods'] });
  void queryClient.invalidateQueries({ queryKey: ['admin', 'exercise'] });
  void queryClient.invalidateQueries({ queryKey: ['admin', 'exercises'] });
}
